/**
 * FOIA Exemption Selector Component
 * Dropdown for selecting FOIA exemptions (b)(1) through (b)(9) with sub-exemptions
 */

import React, { useState } from 'react';
import { ChevronDownIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { FOIA_EXEMPTIONS } from './schema';

const ExemptionSelector = ({
  exemption = '(b)(1)',
  subExemption = null,
  onExemptionChange = () => {},
  onSubExemptionChange = () => {},
  disabled = false,
  showDescription = true,
  size = 'md' // 'sm', 'md', 'lg'
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const selectedExemption = FOIA_EXEMPTIONS.find(ex => ex.value === exemption);
  const hasSubExemptions = selectedExemption?.subExemptions?.length > 0;

  const handleExemptionChange = (e) => {
    const newExemption = e.target.value;
    onExemptionChange(newExemption);
    
    // Reset sub-exemption if the new exemption doesn't support it
    const newExemptionInfo = FOIA_EXEMPTIONS.find(ex => ex.value === newExemption);
    if (!newExemptionInfo?.subExemptions) {
      onSubExemptionChange(null);
    }
  };

  const sizeClasses = {
    sm: 'text-sm px-2 py-1',
    md: 'text-sm px-3 py-2', 
    lg: 'text-base px-4 py-3'
  };

  return (
    <div className="exemption-selector">
      {/* Main exemption dropdown */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          FOIA Exemption
          {showDescription && (
            <div className="relative inline-block ml-2">
              <InformationCircleIcon
                className="h-4 w-4 text-gray-400 cursor-help"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              />
              {showTooltip && selectedExemption && (
                <div className="absolute z-50 bottom-full left-0 mb-2 w-80 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
                  <div className="font-semibold mb-1">{selectedExemption.label}</div>
                  <div>{selectedExemption.description}</div>
                  <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              )}
            </div>
          )}
        </label>
        
        <div className="relative">
          <select
            value={exemption}
            onChange={handleExemptionChange}
            disabled={disabled}
            className={`
              block w-full bg-white border border-gray-300 rounded-md shadow-sm
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
              disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed
              appearance-none cursor-pointer
              ${sizeClasses[size]}
            `}
          >
            {FOIA_EXEMPTIONS.map((ex) => (
              <option key={ex.value} value={ex.value}>
                {ex.value} - {ex.label}
              </option>
            ))}
            {/* Backend can send shapes outside the base nine — subcategories
                like (b)(7)(C) or compound labels. Keep the real value as an
                option so the controlled select doesn't render blank and a
                stray click doesn't silently rewrite the exemption. */}
            {exemption && !FOIA_EXEMPTIONS.find(ex => ex.value === exemption) && (
              <option value={exemption}>{exemption}</option>
            )}
          </select>
          
          <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Sub-exemption dropdown (if applicable) */}
      {hasSubExemptions && (
        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sub-exemption
          </label>
          
          <div className="relative">
            <select
              value={subExemption || ''}
              onChange={(e) => onSubExemptionChange(e.target.value || null)}
              disabled={disabled}
              className={`
                block w-full bg-white border border-gray-300 rounded-md shadow-sm
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed
                appearance-none cursor-pointer
                ${sizeClasses[size]}
              `}
            >
              <option value="">Select sub-exemption (optional)</option>
              {selectedExemption.subExemptions.map((subEx) => (
                <option key={subEx.value} value={subEx.value}>
                  {subEx.label}
                </option>
              ))}
            </select>
            
            <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      )}

      {/* Description text */}
      {showDescription && selectedExemption && (
        <div className="mt-2 p-3 bg-gray-50 rounded-md">
          <div className="text-sm text-gray-700">
            <span className="font-medium">{selectedExemption.label}:</span>{' '}
            {selectedExemption.description}
          </div>
          
          {subExemption && hasSubExemptions && (
            <div className="text-xs text-gray-600 mt-1">
              <span className="font-medium">Sub-exemption:</span>{' '}
              {selectedExemption.subExemptions.find(sub => sub.value === subExemption)?.label}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Quick selector for common exemptions
export const QuickExemptionButtons = ({
  onSelect = () => {},
  disabled = false,
  selectedExemption = null
}) => {
  const commonExemptions = [
    { value: '(b)(1)', label: 'National Security', color: 'red' },
    { value: '(b)(6)', label: 'Personal Privacy', color: 'purple' },
    { value: '(b)(7)(C)', label: 'Law Enforcement Privacy', color: 'orange', subExemption: '(C)' },
    { value: '(b)(4)', label: 'Trade Secrets', color: 'green' },
    { value: '(b)(5)', label: 'Deliberative Process', color: 'blue' }
  ];

  const colorClasses = {
    red: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200',
    purple: 'bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200',
    orange: 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200',
    green: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200',
    blue: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200'
  };

  return (
    <div className="quick-exemption-buttons">
      <div className="text-xs font-medium text-gray-700 mb-2">Quick Select:</div>
      <div className="flex flex-wrap gap-2">
        {commonExemptions.map((exemption) => (
          <button
            key={exemption.value}
            onClick={() => onSelect(exemption.value, exemption.subExemption)}
            disabled={disabled}
            className={`
              px-3 py-1 text-xs font-medium border rounded-full transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
              ${selectedExemption === exemption.value ? 
                colorClasses[exemption.color].replace('hover:', '') : 
                colorClasses[exemption.color]
              }
            `}
          >
            {exemption.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// Compact inline selector for toolbar use
export const CompactExemptionSelector = ({
  exemption = '(b)(1)',
  onExemptionChange = () => {},
  disabled = false
}) => {
  return (
    <div className="relative">
      <select
        value={exemption}
        onChange={(e) => onExemptionChange(e.target.value)}
        disabled={disabled}
        className="
          text-xs bg-white border border-gray-300 rounded px-2 py-1
          focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
          disabled:bg-gray-100 disabled:cursor-not-allowed
          appearance-none cursor-pointer pr-6
        "
      >
        {FOIA_EXEMPTIONS.map((ex) => (
          <option key={ex.value} value={ex.value}>
            {ex.value}
          </option>
        ))}
      </select>
      <ChevronDownIcon className="absolute right-1 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
    </div>
  );
};

export default ExemptionSelector;